// open-local-file.js — "打开本地文件"功能模块
// 负责：按钮绑定 → 文件选择 → 类型分流 → 数据传递 → 页面跳转
// 设计原则：职责单一、有 destroy、有降级

import { saveFileHandle } from '../lib/local-file-store.js';
import { parseDocxToMarkdown } from '../lib/docx-parser.js';
import { showToast } from './file-utils.js';

var _btnOpen = null;

// ===== 初始化 =====
export function initOpenLocalFile() {
  _btnOpen = document.getElementById('btnOpen');
  if (!_btnOpen) return;
  _btnOpen.addEventListener('click', handleOpenClick);
}

// ===== 销毁 =====
export function destroyOpenLocalFile() {
  if (_btnOpen) {
    _btnOpen.removeEventListener('click', handleOpenClick);
    _btnOpen = null;
  }
}

// ===== 文件选择入口 =====
async function handleOpenClick() {
  if (typeof window.showOpenFilePicker === 'function') {
    await openWithFileSystemAPI();
  } else {
    openFallbackFilePicker();
  }
}

function openFallbackFilePicker() {
  var input = document.createElement('input');
  input.type = 'file';
  input.style.display = 'none';
  input.accept = '.md,.txt,.doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx,.csv';
  document.body.appendChild(input);

  input.addEventListener('change', function() {
    var file = input.files[0];
    if (file) routeFile(file, null);
    input.remove();
  });

  // 用户取消选择：focus 时检测 files 是否为空
  window.addEventListener('focus', function cleanup() {
    setTimeout(function() {
      if (input.parentNode && (!input.files || input.files.length === 0)) {
        input.remove();
      }
    }, 100);
    window.removeEventListener('focus', cleanup);
  }, { once: true });

  input.click();
}

async function openWithFileSystemAPI() {
  try {
    var handles = await window.showOpenFilePicker({
      types: [{
        description: '文档文件',
        accept: {
          'text/markdown': ['.md'],
          'text/plain': ['.txt'],
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
          'application/msword': ['.doc'],
          'application/pdf': ['.pdf'],
          'application/vnd.ms-excel': ['.xls'],
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
          'application/vnd.ms-powerpoint': ['.ppt'],
          'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
          'text/csv': ['.csv']
        }
      }],
      multiple: false
    });
    var fileHandle = handles[0];
    var file = await fileHandle.getFile();
    await routeFile(file, fileHandle);
  } catch (e) {
    // 用户取消选择 → 静默，不报错
    if (e.name !== 'AbortError') console.error('[open-local-file]', e);
  }
}

// ===== 类型分流 =====
async function routeFile(file, fileHandle) {
  // 大文件提示
  var MAX_SIZE = 50 * 1024 * 1024; // 50MB
  if (file.size > MAX_SIZE) {
    showToast('文件较大（' + (file.size / 1024 / 1024).toFixed(1) + 'MB），处理可能需要较长时间');
  }

  var ext = file.name.split('.').pop().toLowerCase();

  if (ext === 'md' || ext === 'txt') {
    await openInStory(file, fileHandle, file.type);
  } else if (ext === 'docx') {
    await openDocxInStory(file, fileHandle);
  } else {
    await openInFeishu(file);
  }
}

// ===== 共用：保存 fileHandle + 元信息 → sessionStorage → 跳转 /story =====
async function _saveAndNavigate(file, fileHandle, mime, content) {
  // 1. fileHandle → IndexedDB
  if (fileHandle) {
    try { await saveFileHandle(fileHandle); } catch (e) { /* 降级 */ }
  }

  // 2. 内容预存 sessionStorage
  if (content) {
    try { sessionStorage.setItem('localFileContent', content); } catch (e) { /* 内容过大，自行降级 */ }
  }

  // 3. 元信息 + 导航
  sessionStorage.setItem('localFileMeta', JSON.stringify({
    name: file.name,
    mime: mime,
    size: file.size
  }));
  window.location.href = '/story?source=local-file&name='
    + encodeURIComponent(file.name)
    + '&mime=' + encodeURIComponent(mime);
}

// ===== .md / .txt → /story =====
async function openInStory(file, fileHandle, mime) {
  var content = null;
  try { content = await file.text(); } catch (e) { /* 太大→story端自行重读 */ }
  await _saveAndNavigate(file, fileHandle, mime || 'text/plain', content);
}

// ===== .docx → mammoth 解析 → /story =====
async function openDocxInStory(file, fileHandle) {
  try {
    var result = await parseDocxToMarkdown(file);
    var mime = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    await _saveAndNavigate(file, fileHandle, mime, result.content);
  } catch (e) {
    console.error('[open-local-file] docx 解析失败:', e);
    showToast('无法解析 docx 文件，尝试上传到飞书打开');
    await openInFeishu(file);
  }
}

// ===== .doc / .pdf / .xlsx / .pptx / .csv … → 上传飞书 =====
async function openInFeishu(file) {
  showToast('正在上传到飞书…');

  try {
    var formData = new FormData();
    formData.append('file', file);

    var resp = await fetch('/api/files/upload-to-temp', {
      method: 'POST',
      credentials: 'include',
      body: formData
    });
    var json = await resp.json();

    if (json.success && json.feishuUrl) {
      window.open(json.feishuUrl, '_blank');
    } else {
      showToast('上传失败: ' + (json.error || '未知错误'));
    }
  } catch (e) {
    console.error('[open-local-file] 上传到飞书失败:', e);
    showToast('上传失败: 网络错误');
  }
}
